import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Wrench } from 'lucide-react';
import { getWards, createWard, updateWard, addBed, updateBed } from '../api';
import { useAuth } from '@/hooks/useAuth';
import type { Ward, Bed } from '../types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { FormField } from '@/components/ui/FormField';
import { Input, NativeSelect } from '@/components/ui/Input';
import { RowActionsMenu } from '@/components/ui/RowActionsMenu';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { toast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import { Dash, SettingsToolbar, filterByStatus, matchesSearch, type StatusFilter } from '../shared/SettingsToolbar';

const WARD_TYPES = ['General', 'Semi-Private', 'Private', 'ICU', 'HDU'];
const rupees = (n: number) => `₹${Number(n).toLocaleString('en-IN')}`;

/**
 * Admin → Facility → Wards & Beds: ward types and per-day room rates used by
 * the IP admission flow and discharge billing, at the active branch.
 * List + drawer + ⋯ menu (Admin_Panel_Wireframe.html, D4); a ward's beds are
 * managed inside its drawer. The backend's ward PATCH accepts name, rate,
 * floor and active — not the ward type — so type is read-only once created.
 */
export default function WardsBedsTab() {
  const { branchVersion } = useAuth();
  const [wards, setWards] = useState<Ward[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<Ward | null>(null);

  const load = useCallback(() => getWards().then((w: Ward[]) => { setWards(w); setLoadError(''); }).catch((e: any) => setLoadError(e.message)), []);
  useEffect(() => { load(); }, [load, branchVersion]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast({ title: ok, variant: 'success' }); await load(); }
    catch (e: any) { toast({ title: 'Not saved', description: e.message, variant: 'danger' }); }
  };

  const rows = useMemo(
    () => filterByStatus(wards ?? [], status).filter((w) => matchesSearch(search, w.name, w.ward_type, w.floor)),
    [wards, status, search]
  );
  // Looked up by id so the drawer shows fresh beds after each change.
  const editingWard = typeof editingId === 'number' ? wards?.find((w) => w.id === editingId) ?? null : null;

  const columns: DataTableColumn<Ward>[] = [
    { key: 'name', header: 'Ward', sortable: true, sortValue: (w) => w.name, render: (w) => <span className="font-semibold">{w.name}</span> },
    { key: 'type', header: 'Type', widthClassName: 'w-[130px]', sortable: true, sortValue: (w) => w.ward_type, render: (w) => w.ward_type },
    { key: 'floor', header: 'Floor', widthClassName: 'w-[100px]', render: (w) => w.floor || <Dash /> },
    { key: 'rate', header: 'Daily rate', align: 'right', widthClassName: 'w-[120px]', sortable: true, sortValue: (w) => Number(w.daily_rate), render: (w) => rupees(w.daily_rate) },
    { key: 'beds', header: 'Beds', widthClassName: 'w-[220px]', render: (w) => <BedSummary beds={w.beds} /> },
    { key: 'status', header: 'Status', widthClassName: 'w-[110px]',
      render: (w) => <Badge variant={w.active ? 'success' : 'neutral'}>{w.active ? 'Active' : 'Inactive'}</Badge> },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', widthClassName: 'w-[56px]',
      render: (w) => {
        const occupied = w.beds.filter((b) => b.status === 'occupied').length;
        return (
          <RowActionsMenu
            label={`Actions for ${w.name}`}
            actions={[
              { label: 'Edit ward & beds', onSelect: () => setEditingId(w.id) },
              w.active
                ? { label: 'Deactivate…', danger: true, disabled: occupied > 0, hint: occupied > 0 ? `${occupied} bed${occupied === 1 ? ' is' : 's are'} occupied` : undefined, onSelect: () => setConfirmDeactivate(w) }
                : { label: 'Reactivate', onSelect: () => act(() => updateWard(w.id, { active: true }), `${w.name} reactivated`) },
            ]}
          />
        );
      } },
  ];

  return (
    <div>
      <SettingsToolbar
        search={search}
        onSearch={setSearch}
        placeholder="Search wards"
        status={status}
        onStatus={setStatus}
        count={wards ? `${rows.length} ward${rows.length === 1 ? '' : 's'}` : undefined}
        action={<Button onClick={() => setEditingId('new')}><Plus className="h-4 w-4" /> Add ward</Button>}
      />
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(w) => w.id}
        onRowClick={(w) => setEditingId(w.id)}
        isLoading={!wards && !loadError}
        isError={!!loadError}
        errorMessage={loadError}
        onRetry={load}
        emptyLabel={search ? 'No wards match' : status === 'inactive' ? 'No inactive wards' : 'No wards at this branch yet'}
      />

      <WardSheet
        open={editingId !== null}
        ward={editingWard}
        onClose={() => setEditingId(null)}
        onChanged={load}
        onCreated={async (msg) => { setEditingId(null); toast({ title: msg, variant: 'success' }); await load(); }}
      />

      <ConfirmDialog
        open={!!confirmDeactivate}
        onOpenChange={(o) => { if (!o) setConfirmDeactivate(null); }}
        title={`Deactivate ${confirmDeactivate?.name ?? ''}?`}
        description="Its beds stop appearing for new admissions. Past admissions and bills are unchanged, and you can reactivate it later."
        confirmLabel="Deactivate"
        onConfirm={() => { const w = confirmDeactivate; if (w) act(() => updateWard(w.id, { active: false }), `${w.name} deactivated`); }}
      />
    </div>
  );
}

function BedSummary({ beds }: { beds: Bed[] }) {
  const occupied = beds.filter((b) => b.status === 'occupied').length;
  const maint = beds.filter((b) => b.status === 'maintenance').length;
  return (
    <span className="text-[12.5px]">
      <b>{beds.length}</b> bed{beds.length === 1 ? '' : 's'}
      {occupied > 0 && <span className="text-ink-muted"> · {occupied} occupied</span>}
      {maint > 0 && <span className="text-warn-ink"> · {maint} maintenance</span>}
    </span>
  );
}

const EMPTY = { name: '', ward_type: 'General', daily_rate: '', floor: '', bed_count: '4' };

function WardSheet({ open, ward, onClose, onChanged, onCreated }: {
  open: boolean; ward: Ward | null; onClose: () => void; onChanged: () => Promise<unknown>; onCreated: (msg: string) => void;
}) {
  const isNew = open && !ward;
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newBed, setNewBed] = useState('');
  const wardId = ward?.id;

  useEffect(() => {
    if (!open) return;
    setError(''); setTouched(false); setNewBed('');
    setForm(ward ? { name: ward.name, ward_type: ward.ward_type, daily_rate: String(ward.daily_rate), floor: ward.floor ?? '', bed_count: '' } : EMPTY);
    // Re-seed only when a different ward opens — not when its beds refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, wardId]);

  const set = (k: keyof typeof EMPTY) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const rate = parseFloat(form.daily_rate);
  const nameError = touched && !form.name.trim() ? 'Enter the ward name' : undefined;
  const rateError = touched && !(rate >= 0) ? 'Enter the daily rate' : undefined;

  const save = async () => {
    setTouched(true);
    if (!form.name.trim() || !(rate >= 0)) return;
    setSaving(true); setError('');
    try {
      if (ward) {
        await updateWard(ward.id, { name: form.name.trim(), daily_rate: rate, floor: form.floor.trim() });
        await onChanged();
        toast({ title: `${form.name.trim()} saved`, variant: 'success' });
        onClose();
      } else {
        await createWard({ name: form.name.trim(), ward_type: form.ward_type, daily_rate: rate, floor: form.floor.trim(), bed_count: parseInt(form.bed_count, 10) || 0 });
        onCreated(`${form.name.trim()} added`);
      }
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  const bedAct = async (fn: () => Promise<unknown>) => {
    try { setError(''); await fn(); await onChanged(); } catch (e: any) { setError(e.message); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{isNew ? 'Add ward' : `Edit ${ward?.name ?? 'ward'}`}</SheetTitle>
          <SheetDescription>Wards and beds belong to the branch you are working in.</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">{error}</div>}
          <FormField label="Ward name" required error={nameError}>
            <Input value={form.name} onChange={set('name')} placeholder="e.g. General Ward B" autoFocus />
          </FormField>
          <FormField label="Ward type" hint={isNew ? 'Can’t be changed after the ward is created.' : 'Set when the ward was created.'}>
            <NativeSelect value={form.ward_type} onChange={set('ward_type')} disabled={!isNew}>
              {WARD_TYPES.map((t) => <option key={t}>{t}</option>)}
            </NativeSelect>
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Daily rate (₹)" required error={rateError} hint="Charged per day at discharge.">
              <Input type="number" min={0} value={form.daily_rate} onChange={set('daily_rate')} inputMode="decimal" />
            </FormField>
            <FormField label="Floor">
              <Input value={form.floor} onChange={set('floor')} placeholder="e.g. 2nd" />
            </FormField>
          </div>
          {isNew && (
            <FormField label="Number of beds" hint="Beds are numbered 1, 2, 3… You can add more later.">
              <Input type="number" min={0} value={form.bed_count} onChange={set('bed_count')} />
            </FormField>
          )}

          {ward && (
            <div>
              <div className="lbl">Beds</div>
              <p className="mb-2 text-[11.5px] text-muted-foreground">Green is available, amber occupied, grey under maintenance. Tap an available bed to take it out of service, or a grey one to bring it back; occupied beds can’t be changed.</p>
              <div className="flex flex-wrap gap-2">
                {[...ward.beds].sort((x, y) => x.bed_number.localeCompare(y.bed_number, undefined, { numeric: true })).map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    disabled={b.status === 'occupied'}
                    onClick={() => bedAct(() => updateBed(b.id, { status: b.status === 'maintenance' ? 'available' : 'maintenance' }))}
                    title={b.status === 'occupied' ? 'Occupied' : b.status === 'maintenance' ? 'In maintenance — tap to bring back' : 'Available — tap to mark for maintenance'}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-[12px] font-semibold transition-colors',
                      b.status === 'occupied' && 'cursor-default border-warn/40 bg-warn-soft text-warn-ink',
                      b.status === 'maintenance' && 'border-border bg-muted text-muted-foreground line-through',
                      b.status === 'available' && 'border-success/40 bg-success-soft text-success-ink hover:opacity-80'
                    )}
                  >
                    {b.status === 'maintenance' && <Wrench aria-hidden className="h-3 w-3" />}
                    {b.bed_number}
                  </button>
                ))}
                {ward.beds.length === 0 && <span className="text-[12px] text-muted-foreground">No beds yet.</span>}
              </div>
              <div className="mt-3 flex gap-2">
                <Input value={newBed} onChange={(e) => setNewBed(e.target.value)} placeholder="New bed number" className="max-w-[160px]"
                  onKeyDown={(e) => { if (e.key === 'Enter' && newBed.trim()) bedAct(async () => { await addBed(ward.id, { bed_number: newBed.trim() }); setNewBed(''); }); }} />
                <Button variant="ghost" disabled={!newBed.trim()} onClick={() => bedAct(async () => { await addBed(ward.id, { bed_number: newBed.trim() }); setNewBed(''); })}>
                  <Plus className="h-4 w-4" /> Add bed
                </Button>
              </div>
            </div>
          )}
        </SheetBody>
        <SheetFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving}>{isNew ? 'Add ward' : 'Save changes'}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
